<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		ActionsContainer,
		AdvancedCard,
		AdvancedCardV2,
		Alert,
		AlertV2,
		Button,
		ButtonV2,
		Checkbox,
		IconButton,
		Input,
		InputV2,
		Loader,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system';
	import { real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { validateIpWithPort } from 'src/helpers/validators';
	import { devices_config } from '@devices/index';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	const DEFAULT_MULTICAST_ADDR = '224.0.0.163:3333';
	const MAX_INSTANCES = 5;
	const STATS_INTERVAL_MS = 15000;

	export let device: Device;
	export let isOffline: boolean;

	const pickRpc = (res: SettingRequest): Record<string, unknown> | null =>
		(res?.result ?? res?.data ?? null) as Record<string, unknown> | null;

	const rid = real_id(device.id);

	$: isEmeterDevice = devices_config?.[device?.type]?.family === 'emeter';

	/** Device and config context required before any LNM RPC or reading `devices_settings`. */
	$: lnmPrerequisitesMet =
		isEmeterDevice &&
		device != null &&
		device.id != null &&
		device.type != null &&
		String(device.id).length > 0;

	/**
	 * Ensures each `lnm:N` block from Shelly.GetConfig is a non-null object before we merge
	 * into the store or poll stats (avoids bad/partial config taking down the panel).
	 */
	const verifyLnmSectionsInShellyConfig = (cfg: Record<string, unknown> | null): boolean => {
		if (cfg == null || typeof cfg !== 'object') return false;
		for (const k of Object.keys(cfg)) {
			if (!/^lnm:\d+$/.test(k)) continue;
			const section = cfg[k];
			if (section == null || typeof section !== 'object') return false;
		}
		return true;
	};

	const lnmIdFromKey = (key: string) => Number(key.split(':')[1]);

	$: settingsStore = getDeviceSettings(rid);
	$: settings = $settingsStore || {};
	$: AdvancedCardComponent = $showNewLayout ? AdvancedCardV2 : AdvancedCard;
	$: ButtonComponent = $showNewLayout ? ButtonV2 : Button;
	$: InputComponent = $showNewLayout ? InputV2 : Input;

	$: instanceKeys = Object.keys(settings)
		.filter((k) => /^lnm:\d+$/.test(k))
		.sort((a, b) => lnmIdFromKey(a) - lnmIdFromKey(b));

	$: availableEm1Keys = Object.keys(settings)
		.filter((k) => /^(em|switch):\d+$/.test(k))
		.sort((a, b) => Number(a.split(':')[1]) - Number(b.split(':')[1]));

	let instanceStats: Record<number, { tx_msgs?: number; rx_msgs?: number; since?: number }> = {};
	let statsTimer: ReturnType<typeof setInterval> | undefined;

	let waitDelete = false;
	let waitSaveEdit = false;

	let showDeleteModal = false;
	let deleteTargetId: number | null = null;

	let showEditModal = false;
	/** When true the shared config modal creates a new instance; otherwise it edits an existing one. */
	let isCreateMode = false;
	let editNumericId: number | null = null;
	let editAddr = '';
	let editAddrInitial = '';
	let editTxEnable = false;
	let editRxEnable = false;
	let editRpcEnable = false;
	let editTxComponents: string[] = [];
	// let editFilters: { device: string; status: string }[] = [];
	let editPsk = '';
	let editError: string | undefined;

	let shellyLnmConfigChecked = false;
	let shellyLnmConfigShapeOk = false;

	const refreshShellyConfig = async (): Promise<boolean> => {
		const res: SettingRequest = await gen2Request(device, 'Shelly.GetConfig', {});
		if (!res.isok) return false;
		const incoming = pickRpc(res);
		if (!incoming || typeof incoming !== 'object') return false;
		if (!verifyLnmSectionsInShellyConfig(incoming as Record<string, unknown>)) return false;
		const prev = readDeviceSettings(rid) || {};
		devices_settings.update_devices_settings(rid, { ...prev, ...incoming });
		return true;
	};

	const refreshStats = async () => {
		if (isOffline || !isEmeterDevice) return;
		const st = readDeviceSettings(rid) || {};
		const keys = Object.keys(st)
			.filter((k) => /^lnm:\d+$/.test(k))
			.sort((a, b) => lnmIdFromKey(a) - lnmIdFromKey(b));
		const next: typeof instanceStats = { ...instanceStats };
		for (const key of keys) {
			const id = lnmIdFromKey(key);
			const res: SettingRequest = await gen2Request(device, 'LNM.GetStatus', { id });
			const data = pickRpc(res);
			if (res.isok && data && data.stats && typeof data.stats === 'object') {
				next[id] = data.stats as { tx_msgs?: number; rx_msgs?: number; since?: number };
			}
		}
		instanceStats = next;
	};

	/** Opens the shared config modal pre-filled with sensible defaults for a brand new instance. */
	const openCreate = () => {
		isCreateMode = true;
		editNumericId = null;
		editError = undefined;
		editAddr = DEFAULT_MULTICAST_ADDR;
		editAddrInitial = DEFAULT_MULTICAST_ADDR;
		editTxEnable = false;
		editRxEnable = false;
		editRpcEnable = false;
		editTxComponents = [];
		// editFilters = [];
		editPsk = '';
		showEditModal = true;
	};

	const confirmDelete = (id: number) => {
		deleteTargetId = id;
		showDeleteModal = true;
	};

	const deleteInstance = async () => {
		if (deleteTargetId == null) return;
		waitDelete = true;
		const res: SettingRequest = await gen2Request(device, 'LNM.Delete', { id: deleteTargetId });
		waitDelete = false;
		if (res.isok) {
			onSettingsResponse(res, device, {
				success: $format('lnm_delete_success', { default: 'LNM instance removed.' }),
			});
			showDeleteModal = false;
			deleteTargetId = null;
			await refreshShellyConfig();
			await refreshStats();
		} else {
			onSettingsResponse(res, device, {
				danger: $format('lnm_delete_error', { default: 'Could not remove LNM instance.' }),
			});
		}
	};

	const openEdit = async (key: string) => {
		const id = lnmIdFromKey(key);
		isCreateMode = false;
		editNumericId = id;
		editError = undefined;
		const res: SettingRequest = await gen2Request(device, 'LNM.GetConfig', { id });
		const data = pickRpc(res);
		if (!res.isok || !data) {
			onSettingsResponse(res, device, {
				danger: $format('lnm_load_error', { default: 'Could not load LNM configuration.' }),
			});
			return;
		}
		const addr = typeof data.addr === 'string' ? data.addr : '';
		editAddr = addr;
		editAddrInitial = addr;
		const tx = data.tx as { enable?: boolean; components?: string[] } | undefined;
		const rx = data.rx as
			| {
					enable?: boolean;
					filters?: { device?: string; status?: string }[];
			  }
			| undefined;
		editTxEnable = tx?.enable !== false;
		editRxEnable = rx?.enable === true;
		editRpcEnable = data?.rpc_enable === true;
		editTxComponents = Array.isArray(tx?.components) ? [...tx.components] : [];
		// editFilters = Array.isArray(rx?.filters)
		// 	? rx.filters.map((f) => ({
		// 			device: typeof f?.device === 'string' ? f.device : '',
		// 			status: typeof f?.status === 'string' ? f.status : '',
		// 		}))
		// 	: [];
		editPsk = '';
		showEditModal = true;
	};

	const toggleTxComponent = (key: string) => {
		if (editTxComponents.includes(key)) {
			editTxComponents = editTxComponents.filter((k) => k !== key);
		} else {
			editTxComponents = [...editTxComponents, key];
		}
	};

	const toggleEditRpcxEnable = () => {
		editRpcEnable = !editRpcEnable;
	};

	const toggleEditRxEnable = () => {
		editRxEnable = !editRxEnable;
	};

	const toggleEditTxEnable = () => {
		editTxEnable = !editTxEnable;
	};

	// const addFilterRow = () => {
	// 	editFilters = [...editFilters, { device: '', status: '' }];
	// };

	// const removeFilterRow = (index: number) => {
	// 	editFilters = editFilters.filter((_, i) => i !== index);
	// };

	/**
	 * Builds the TX/RX/RPC settings shared by the create follow-up and edit flows. `addr` is
	 * intentionally excluded: it is set once via `LNM.Create`, and changing it afterwards requires
	 * a device restart, so it is only sent from the edit flow.
	 */
	const buildLnmSettings = (): Record<string, unknown> => {
		const config: Record<string, unknown> = {
			tx: {
				enable: editTxEnable,
				components: [...editTxComponents],
			},
			rpc_enable: editRpcEnable,
			rx: {
				enable: editRxEnable,
				// filters,
			},
		};
		if (editPsk.trim().length > 0) {
			config.psk = editPsk.trim();
		}
		return config;
	};

	/**
	 * Creates an instance in two steps, mirroring the embedded web UI and the LNM API:
	 * 1. `LNM.Create` with only `{ addr }` to allocate the instance and obtain its id.
	 * 2. `LNM.SetConfig` on that id with the remaining TX/RX/RPC settings.
	 * @see https://shelly-api-docs.shelly.cloud/gen2/DynamicComponents/LNM/
	 */
	const createInstance = async (addr: string) => {
		if (instanceKeys.length >= MAX_INSTANCES) return;
		waitSaveEdit = true;
		const createRes: SettingRequest = await gen2Request(device, 'LNM.Create', {
			config: { addr },
		});
		const errorCode = createRes?.error?.device_error?.code ?? createRes?.errors?.code;
		if (!createRes.isok) {
			waitSaveEdit = false;
			const danger =
				errorCode === -108 || errorCode === 108
					? $format('lnm_max_instances_error', {
							default: 'Maximum number of LNM instances reached.',
						})
					: undefined;
			onSettingsResponse({ ...createRes, isok: false }, device, {
				danger:
					danger || $format('lnm_create_error', { default: 'Could not create LNM instance.' }),
			});
			return;
		}

		const created = pickRpc(createRes);
		const newId = typeof created?.id === 'number' ? (created.id as number) : null;
		if (newId != null) {
			const setRes: SettingRequest = await gen2Request(device, 'LNM.SetConfig', {
				id: newId,
				config: buildLnmSettings(),
			});
			if (!setRes.isok) {
				waitSaveEdit = false;
				onSettingsResponse({ ...setRes, isok: false }, device, {
					danger: $format('lnm_create_settings_error', {
						default: 'Instance created, but its settings could not be applied.',
					}),
				});
				showEditModal = false;
				await refreshShellyConfig();
				await refreshStats();
				return;
			}
		}

		waitSaveEdit = false;
		onSettingsResponse(createRes, device, {
			success: $format('lnm_create_success', {
				default: 'Local Network Messaging instance created.',
			}),
		});
		showEditModal = false;
		await refreshShellyConfig();
		await refreshStats();
	};

	const updateInstance = async (addr: string) => {
		if (editNumericId == null) return;
		waitSaveEdit = true;
		const res: SettingRequest = await gen2Request(device, 'LNM.SetConfig', {
			id: editNumericId,
			config: { addr, ...buildLnmSettings() },
		});
		waitSaveEdit = false;
		if (res.isok) {
			const addrChanged = addr !== editAddrInitial;
			onSettingsResponse(res, device, {
				success: addrChanged
					? $format('lnm_save_reboot', {
							default:
								'Settings saved. Changing the multicast address may require a device reboot.',
						})
					: $format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
			});
			showEditModal = false;
			await refreshShellyConfig();
			await refreshStats();
		} else {
			onSettingsResponse(res, device, {
				danger: $format('setting_err_toast', { default: 'Failed to apply settings.' }),
			});
		}
	};

	const saveInstance = async () => {
		if (waitSaveEdit) return;
		const addr = (editAddr || '').trim();
		if (!validateIpWithPort(addr)) {
			editError = $format('lnm_invalid_addr', { default: 'Enter a valid multicast IP and port.' });
			return;
		}
		const addressAlreadyConfigured = instanceKeys.some((key) => {
			if (!isCreateMode && lnmIdFromKey(key) === editNumericId) return false;
			return typeof settings[key]?.addr === 'string' && settings[key].addr.trim() === addr;
		});
		if (addressAlreadyConfigured) {
			editError = $format('lnm_addr_in_use_error', {
				default: 'This multicast address and port is already used by another LNM instance.',
			});
			return;
		}
		editError = undefined;
		if (isCreateMode) {
			await createInstance(addr);
		} else {
			await updateInstance(addr);
		}
	};

	onMount(async () => {
		if (!lnmPrerequisitesMet) {
			shellyLnmConfigChecked = true;
			shellyLnmConfigShapeOk = false;
			return;
		}
		const configOk = await refreshShellyConfig();
		shellyLnmConfigChecked = true;
		shellyLnmConfigShapeOk = configOk;
		if (!configOk) return;
		await refreshStats();
		statsTimer = setInterval(refreshStats, STATS_INTERVAL_MS);
	});

	onDestroy(() => {
		if (statsTimer) clearInterval(statsTimer);
	});

	$: addrChangedWarning =
		showEditModal &&
		!isCreateMode &&
		editAddr.trim() !== '' &&
		editAddr.trim() !== editAddrInitial.trim();
</script>

{#if !lnmPrerequisitesMet}
	{#if $showNewLayout}
		<AlertV2
			isHeadingBold
			customIcon="fas fa-exclamation-triangle"
			heading={$format('label_important', { default: 'Important' })}
			class="mb-s"
			message={$format('lnm_device_unavailable', {
				default:
					'This device is not available yet. Local Network Messaging cannot be loaded or configured.',
			})}
		/>
	{:else}
		<Alert
			type="warning"
			isHeadingBold
			heading={$format('label_important', { default: 'Important' })}
			class="mb-s"
			message={$format('lnm_device_unavailable', {
				default:
					'This device is not available yet. Local Network Messaging cannot be loaded or configured.',
			})}
		/>
	{/if}
{:else if !shellyLnmConfigChecked}
	<div class="d-flex justify-content-center">
		<Loader size="l" />
	</div>
{:else if !shellyLnmConfigShapeOk}
	{#if $showNewLayout}
		<AlertV2
			isHeadingBold
			customIcon="fas fa-exclamation-triangle"
			heading={$format('label_info', { default: 'Important' })}
			class="mb-s"
			message={$format('lnm_config_invalid', {
				default:
					'Local Network Messaging data from the device could not be applied. Try again after refresh or check device firmware.',
			})}
		/>
	{:else}
		<Alert
			type="warning"
			isHeadingBold
			heading={$format('label_info', { default: 'Important' })}
			class="mb-s"
			message={$format('lnm_config_invalid', {
				default:
					'Local Network Messaging data from the device could not be applied. Try again after refresh or check device firmware.',
			})}
		/>
	{/if}
{:else}
	<Text class="mb-s" styleType="body-small">
		{$format('lnm_description', {
			default:
				'Local Network Messaging sends device status over UDP multicast. Up to five independent instances are supported.',
		})}
	</Text>
	{#if !$showNewLayout}
		<hr class="mb-s" />
	{/if}
	{#if $showNewLayout}
		<AlertV2
			isHeadingBold
			customIcon="fas fa-exclamation-triangle"
			heading={$format('label_info', { default: 'Important' })}
			class="mb-s"
			message={$format('lnm_addr_reboot_hint', {
				default:
					'Changing the multicast address may require a reboot so the device can rejoin the multicast group.',
			})}
		/>
	{:else}
		<Alert
			type="warning"
			isHeadingBold
			heading={$format('label_info', { default: 'Important' })}
			class="mb-s"
			message={$format('lnm_addr_reboot_hint', {
				default:
					'Changing the multicast address may require a reboot so the device can rejoin the multicast group.',
			})}
		/>
	{/if}

	{#if instanceKeys.length === 0}
		<Text class="mb-s" styleType="body-small">
			{$format('lnm_no_instances', {
				default: 'No LNM instances yet. Create one to get started.',
			})}
		</Text>
	{:else}
		{#each instanceKeys as key (key)}
			{@const lnmCardId = lnmIdFromKey(key)}
			{@const lnmAddr =
				typeof settings[key]?.addr === 'string' && settings[key].addr ? settings[key].addr : ''}
			{@const lnmStats = instanceStats[lnmCardId]}
			{@const lnmRpcEnable = settings[key]?.rpc_enable
				? $format('xt1_template_enabled', { default: 'Enabled' })
				: $format('xt1_template_disabled', { default: 'Disabled' })}
			{@const lnmCardTitle = $format('lnm_instance_title', {
				values: { id: lnmCardId },
				default: 'LNM instance ({id})',
			})}
			{@const lnmStatsDescription = lnmStats
				? $format('lnm_stats_tx', { default: 'TX messages' }) +
					': ' +
					(lnmStats.tx_msgs ?? '—') +
					' · ' +
					$format('lnm_stats_rx', { default: 'RX messages' }) +
					': ' +
					(lnmStats.rx_msgs ?? '—') +
					' · ' +
					$format('lnm_stats_rpc', { default: 'RPC' }) +
					': ' +
					lnmRpcEnable
				: null}
			<svelte:component
				this={AdvancedCardComponent}
				class="mb-s"
				title={lnmCardTitle}
				subtitle={lnmAddr ? { value: lnmAddr, bold: false, textSmall: true } : null}
				description={lnmStatsDescription
					? { value: lnmStatsDescription, textSmall: true }
					: null}
				aria-label={lnmAddr ? lnmCardTitle + ' · ' + lnmAddr : lnmCardTitle}
				isFocusable={false}
			>
				<ActionsContainer orientation="vertical" type="simple" slot="right">
					<svelte:fragment slot="first">
						<IconButton
							aria-label={$format('lnm_edit_instance', { default: 'Configure' })}
							icon="fas fa-pen"
							variant="primary"
							disabled={isOffline}
							on:click={(e) => {
								e.stopPropagation();
								openEdit(key);
							}}
						/>
					</svelte:fragment>
					<IconButton
						slot="second"
						aria-label={$format('lnm_delete_instance', { default: 'Delete' })}
						icon="fas fa-trash"
						variant="danger"
						disabled={isOffline}
						on:click={(e) => {
							e.stopPropagation();
							confirmDelete(lnmIdFromKey(key));
						}}
					/>
				</ActionsContainer>
			</svelte:component>
		{/each}
	{/if}

	<svelte:component
		this={ButtonComponent}
		size="m"
		width="full"
		class="mt-xs"
		disabled={isOffline || instanceKeys.length >= MAX_INSTANCES}
		on:click={openCreate}
	>
		{$format('lnm_create_instance', { default: 'Add LNM instance' })}
	</svelte:component>

	{#if !$showNewLayout && instanceKeys.length >= MAX_INSTANCES}
		<Text styleType="body-small" class="mt-s">
			{$format('lnm_max_instances', { default: 'Maximum of 5 instances reached.' })}
		</Text>
	{/if}

	<Modal
		showModal={showDeleteModal}
		backdropClose={true}
		title={$format('lnm_delete_instance', { default: 'Delete' })}
		on:close={() => {
			showDeleteModal = false;
			deleteTargetId = null;
		}}
	>
		<svelte:fragment slot="content">
			<Text>
				{$format('lnm_delete_confirm', {
					default: 'Remove this LNM instance? This cannot be undone.',
				})}
			</Text>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<svelte:component
				this={ButtonComponent}
				class="mr-s"
				variant="secondary"
				on:click={() => (showDeleteModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</svelte:component>
			<svelte:component
				this={ButtonComponent}
				variant="danger"
				disabled={isOffline}
				loading={waitDelete}
				on:click={deleteInstance}
			>
				{$format('lnm_delete_instance', { default: 'Delete' })}
			</svelte:component>
		</div>
	</Modal>

	<Modal
		showModal={showEditModal}
		backdropClose={true}
		title={isCreateMode
			? capFirstChar($format('lnm_create_instance', { default: 'Add LNM instance' }))
			: capFirstChar($format('lnm_edit_instance', { default: 'Configure LNM' }))}
		on:close={() => (showEditModal = false)}
		removeScrollPadding
	>
		<div slot="content" class="pl-m pr-m">
			{#if addrChangedWarning}
				{#if $showNewLayout}
					<AlertV2
						class="mb-s"
						customIcon="fas fa-exclamation-triangle"
						heading={$format('label_info', { default: 'Important' })}
						message={$format('lnm_addr_reboot_hint', {
							default:
								'Changing the multicast address may require a reboot so the device can rejoin the multicast group.',
						})}
					/>
				{:else}
					<Alert
						type="warning"
						class="mb-s"
						heading={$format('label_info', { default: 'Important' })}
						message={$format('lnm_addr_reboot_hint', {
							default:
								'Changing the multicast address may require a reboot so the device can rejoin the multicast group.',
						})}
					/>
				{/if}
			{/if}

			<svelte:component
				this={InputComponent}
				id="input-lnm-edit-addr"
				aria-labelledby="input-lnm-edit-addr"
				size="m"
				class="mb-s"
				label={$format('lnm_multicast_address', {
					default: 'Multicast address and port',
				})}
				info={$format('lnm_addr_info', {
					default:
						'The IP address must be in the IPv4 multicast range (i.e. 224.0.0.0 to 239.255.255.255)',
				})}
				bind:value={editAddr}
				error={editError}
				disabled={isOffline}
				on:input={() => (editError = undefined)}
			/>

			<Checkbox
				id="checkbox-lnm-rpc-enable"
				size="m"
				class="mb-s"
				checked={editRpcEnable}
				disabled={isOffline}
				on:click={toggleEditRpcxEnable}
				info={$format('lnm_rpc_enable_info', {
					default: 'Allow other devices to send RPC commands to this device via LNM',
				})}
			>
				{$format('lnm_rpc_enable', { default: 'Enable RPC (Remote Procedure Calls)' })}
			</Checkbox>

			<Checkbox
				id="checkbox-lnm-rx-enable"
				size="m"
				class="mb-s"
				checked={editRxEnable}
				disabled={isOffline}
				on:click={toggleEditRxEnable}
				info={$format('lnm_rx_enable_info', {
					default: 'Listen for multicast messages from other devices',
				})}
			>
				{$format('lnm_rx_enable', { default: 'Enable RX (Receive)' })}
			</Checkbox>

			<Checkbox
				id="checkbox-lnm-tx-enable"
				size="m"
				class={editTxEnable ? 'mb-s' : ''}
				checked={editTxEnable}
				disabled={isOffline}
				on:click={toggleEditTxEnable}
				info={$format('lnm_tx_enable_info', {
					default: 'Broadcast status updates to the multicast group',
				})}
			>
				{$format('lnm_tx_enable', { default: 'Enable TX (Transmit)' })}
			</Checkbox>

			{#if editTxEnable}
				<Text styleType="body-small" class="mb-xs ml-xs">
					{$format('lnm_tx_components', { default: 'Components to broadcast' })}
				</Text>
				{#if availableEm1Keys.length === 0}
					<Text styleType="body-small" class="ml-xs">
						{$format('lnm_no_em1', {
							default: 'No EM channels found in settings; TX selection may be limited.',
						})}
					</Text>
				{:else}
					<div class="d-flex flex-wrap">
						{#each availableEm1Keys as emKey (emKey)}
							<Checkbox
								id={`checkbox-lnm-tx-${emKey.replace(/:/g, '-')}`}
								size="m"
								class="ml-xs"
								checked={editTxComponents.includes(emKey)}
								disabled={isOffline}
								on:click={() => toggleTxComponent(emKey)}
							>
								{emKey}
							</Checkbox>
						{/each}
					</div>
				{/if}
			{/if}

			<!-- 
					For now this is hidden, because of the inconsistency between the app UI and the embedded web UI.
					Once we discuss and clarify the UI, we can show this again or remove it entirely.
				-->

			<!-- <Text styleType="body" class="mb-xs font-weight-semibold">
					{$format('lnm_rx_filters', { default: 'RX filters' })}
				</Text>
				{#each editFilters as f, i (i)}
					<div class="d-flex flex-wrap gap-s mb-s align-items-end">
						<svelte:component
							this={InputComponent}
							id="input-lnm-filter-dev-{i}"
							aria-labelledby="input-lnm-filter-dev-{i}"
							size="m"
							class="flex-grow-1"
							label={$format('lnm_filter_device_pattern', { default: 'Device pattern' })}
							bind:value={f.device}
							disabled={isOffline}
						/>
						<svelte:component
							this={InputComponent}
							id="input-lnm-filter-st-{i}"
							aria-labelledby="input-lnm-filter-st-{i}"
							size="m"
							class="flex-grow-1"
							label={$format('lnm_filter_status_pattern', { default: 'Status pattern' })}
							bind:value={f.status}
							disabled={isOffline}
						/>
						<svelte:component
							this={ButtonComponent}
							size="m"
							variant="secondary"
							on:click={() => removeFilterRow(i)}
						>
							{$format('button_remove', { default: 'Remove' })}
						</svelte:component>
					</div>
				{/each}
				<svelte:component
					this={ButtonComponent}
					size="m"
					variant="secondary"
					class="mb-m"
					on:click={addFilterRow}
					disabled={isOffline}
				>
					{$format('lnm_add_filter', { default: 'Add filter' })}
				</svelte:component>

				<svelte:component
					this={InputComponent}
					id="input-lnm-psk"
					aria-labelledby="input-lnm-psk"
					a11yProps={{
						iconButtonAriaLabel: capFirstChar(
							$format('label_password', { default: 'Password' }),
						),
					}}
					size="m"
					class="mb-m"
					type="password"
					label={$format('lnm_psk_optional', {
						default: 'Pre-shared key (optional, leave blank to leave unchanged)',
					})}
					bind:value={editPsk}
					disabled={isOffline}
					autocomplete="new-password"
				/> -->
		</div>
		<div class="d-flex justify-content-end" slot="footer">
			<svelte:component
				this={ButtonComponent}
				class="mr-s"
				variant="secondary"
				on:click={() => (showEditModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</svelte:component>
			<svelte:component
				this={ButtonComponent}
				disabled={isOffline || waitSaveEdit}
				loading={waitSaveEdit}
				on:click={saveInstance}
			>
				{$format('lnm_save', { default: 'Save' })}
			</svelte:component>
		</div>
	</Modal>
{/if}
